import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { invalidateInventoryQueries } from "@/lib/mutationHelpers";
import { queryKeys } from "@/lib/queryKeys";
import { normalizeRoomName } from "@/lib/roomUtils";

export interface InventoryRoom {
  id: string;
  property_id: string;
  name: string;
  order_index: number;
  created_at: string;
  /** Code to enter this room (e.g. lockbox). Shown to cleaner. */
  entry_code?: string | null;
  /** Notes for entering this room. Shown to cleaner. */
  entry_notes?: string | null;
  /** Canonical room (single source of truth). */
  property_room_id?: string | null;
}

export const useInventoryRooms = (propertyId: string | undefined) => {
  return useQuery({
    queryKey: ["inventory-rooms", propertyId],
    queryFn: async () => {
      if (!propertyId) return [];
      const { data, error } = await supabase
        .from("inventory_rooms")
        .select("*")
        .eq("property_id", propertyId)
        .order("order_index", { ascending: true });

      if (error) throw error;
      return data as InventoryRoom[];
    },
    enabled: !!propertyId,
  });
};

export const useCreateInventoryRoom = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      propertyId,
      name,
      itemIds,
      entryCode,
      entryNotes,
    }: {
      propertyId: string;
      name: string;
      itemIds?: string[];
      entryCode?: string | null;
      entryNotes?: string | null;
    }) => {
      const trimmed = name.trim();
      if (!trimmed) throw new Error("Room name is required");

      const nameNorm = normalizeRoomName(trimmed);

      // 1) Find or create property_rooms row (canonical)
      const { data: existingPropRooms } = await supabase
        .from("property_rooms")
        .select("id, name")
        .eq("property_id", propertyId)
        .eq("name_normalized", nameNorm)
        .maybeSingle();

      let propertyRoomId: string;
      let canonicalName: string;
      if (existingPropRooms) {
        propertyRoomId = existingPropRooms.id;
        canonicalName = existingPropRooms.name;
      } else {
        const { data: newPropRoom, error: prErr } = await supabase
          .from("property_rooms")
          .insert({ property_id: propertyId, name: trimmed, name_normalized: nameNorm })
          .select("id, name")
          .single();
        if (prErr) {
          if (prErr.code === "23505") throw new Error("A room with this name already exists");
          throw prErr;
        }
        propertyRoomId = newPropRoom!.id;
        canonicalName = newPropRoom!.name;
      }

      // 2) Reuse existing inventory_rooms for this property_room_id if any
      const { data: existingInvRoom } = await supabase
        .from("inventory_rooms")
        .select("id, property_id, name, order_index, created_at, entry_code, entry_notes, property_room_id")
        .eq("property_id", propertyId)
        .eq("property_room_id", propertyRoomId)
        .maybeSingle();

      if (existingInvRoom) {
        if (itemIds && itemIds.length > 0) {
          await supabase.from("inventory_items").update({ room_id: existingInvRoom.id }).in("id", itemIds);
        }
        return existingInvRoom as InventoryRoom;
      }

      // 3) Get max order_index and create inventory_rooms linked to property_room
      const { data: maxOrder } = await supabase
        .from("inventory_rooms")
        .select("order_index")
        .eq("property_id", propertyId)
        .order("order_index", { ascending: false })
        .limit(1)
        .maybeSingle();

      const nextOrder = (maxOrder?.order_index ?? -1) + 1;

      const { data: room, error } = await supabase
        .from("inventory_rooms")
        .insert({
          property_id: propertyId,
          name: canonicalName,
          order_index: nextOrder,
          entry_code: entryCode ?? null,
          entry_notes: entryNotes ?? null,
          property_room_id: propertyRoomId,
        })
        .select()
        .single();

      if (error) throw error;

      if (itemIds && itemIds.length > 0) {
        await supabase.from("inventory_items").update({ room_id: room.id }).in("id", itemIds);
      }

      return room as InventoryRoom;
    },
    onSuccess: async (room, variables) => {
      queryClient.setQueryData(["inventory-rooms", variables.propertyId], (old: InventoryRoom[] | undefined) => {
        const list = old ?? [];
        if (list.some((r) => r.id === room.id)) return list;
        return [...list, room];
      });
      queryClient.invalidateQueries({ queryKey: ["inventory-rooms", variables.propertyId] });
      queryClient.invalidateQueries({ queryKey: queryKeys.propertyRooms.byProperty(variables.propertyId) });
      await invalidateInventoryQueries(queryClient, variables.propertyId);
    },
  });
};

export const useUpdateInventoryRoom = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      id,
      propertyId,
      name,
      entryCode,
      entryNotes,
    }: {
      id: string;
      propertyId: string;
      name: string;
      entryCode?: string | null;
      entryNotes?: string | null;
    }) => {
      const updates: { name: string; entry_code?: string | null; entry_notes?: string | null } = { name };
      if (entryCode !== undefined) updates.entry_code = entryCode;
      if (entryNotes !== undefined) updates.entry_notes = entryNotes;
      const { data, error } = await supabase
        .from("inventory_rooms")
        .update(updates)
        .eq("id", id)
        .select()
        .single();

      if (error) throw error;
      return data as InventoryRoom;
    },
    onSuccess: async (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["inventory-rooms", variables.propertyId] });
    },
  });
};

export const useDeleteInventoryRoom = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, propertyId }: { id: string; propertyId: string }) => {
      // First unassign all items from this room
      await supabase
        .from("inventory_items")
        .update({ room_id: null })
        .eq("room_id", id);

      // Then delete the room
      const { error } = await supabase.from("inventory_rooms").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: async (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["inventory-rooms", variables.propertyId] });
      await invalidateInventoryQueries(queryClient, variables.propertyId);
    },
  });
};

export const useReorderInventoryRooms = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      propertyId,
      roomIds,
    }: {
      propertyId: string;
      roomIds: string[];
    }) => {
      // Update order_index for each room
      for (let i = 0; i < roomIds.length; i++) {
        const { error } = await supabase
          .from("inventory_rooms")
          .update({ order_index: i })
          .eq("id", roomIds[i]);
        if (error) throw error;
      }
    },
    onSuccess: async (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["inventory-rooms", variables.propertyId] });
    },
  });
};

export const useUpdateItemRoom = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      itemId,
      propertyId,
      roomId,
    }: {
      itemId: string;
      propertyId: string;
      roomId: string | null;
    }) => {
      const { error } = await supabase
        .from("inventory_items")
        .update({ room_id: roomId })
        .eq("id", itemId);

      if (error) throw error;
    },
    onSuccess: async (_, variables) => {
      await invalidateInventoryQueries(queryClient, variables.propertyId);
    },
  });
};
